/*
 * Onboarding 002 — the bespoke screens.
 *
 * Sixteen of the 54 have real UI of their own rather than being a question and a column of
 * cards. The rest are config in quiz-002-data.js.
 *
 * Loaded after quiz-002-ui.js and quiz-002-api.js, before quiz-002.js.
 */
window.ReformlyQuiz = window.ReformlyQuiz || {};

(function (NS) {
  'use strict';

  var U = NS.ui, C = NS.calc, el = U.el, svg = U.svg, S = function () { return NS.store.state; };
  NS.custom = {};

  /* ─── Shared pieces ──────────────────────────────────────────────────────── */

  /** Surface card with a green check beside a heading — D11, D15 and the username states. */
  function noticeCard(title, bodyNode, iconMarkup) {
    return el('div.o2-notice', null, [
      el('div.o2-notice-head', null, [
        svg(iconMarkup || U.ICON.checkGreen),
        el('span.o2-notice-title', { text: title }),
      ]),
      bodyNode,
    ]);
  }

  /** D3's BMI card and D4's change card. Both are cards, not loose text. */
  function statCard(o) {
    var kids = [el('div.o2-stat-row', null, [
      el('span.o2-stat-label', { text: o.label }),
      el('span.o2-stat-value', { text: o.value }),
    ])];
    if (o.note) {
      kids.push(el('div.o2-stat-note', null, [svg(U.ICON.checkGreen), el('span', { text: o.note })]));
    }
    kids.push(el('p.o2-stat-body', { text: o.body }));
    return el('div.o2-stat', null, kids);
  }

  /**
   * Wheel picker — a real scroll-snap list, not a simulated drag.
   *
   * Container 196, rows a uniform 44, which is exactly what the design's selection band at
   * top:76 implies ((196-44)/2). The static frame draws rows at 36/40/44/40/36 to suggest
   * perspective; implementing that literally would make the snap positions non-linear and
   * the scroll would drift out of step with the highlighted row.
   */
  /**
   * The value wheel on D2b/D3b/D4b/D5b.
   *
   * Modelled on mobile-select's approach rather than a scroll container or Swiper:
   *
   *   - the list is a <ul> moved with translate3d, not something the browser scrolls
   *   - dragging is 1:1 with the finger, with NO momentum
   *   - releasing snaps to the nearest row over 180ms
   *   - a fixed band marks the selection; rows are not restyled as they pass under it
   *
   * The momentum is what made the earlier versions feel wrong: a flick threw the list
   * several rows past where it was released, and rows swept past the centre could not be
   * kept in step with it. Direct dragging always tracks the finger, so the wheel goes
   * exactly where it is put.
   *
   * Geometry: container 196, rows 44, so the centre row sits PAD=76 from the top. Distance
   * for value index i is PAD - i*ROW, which inverts to round((PAD - distance) / ROW).
   */
  function wheelPicker(o) {
    var ROW = 44, H = 196, PAD = (H - ROW) / 2;
    var count = o.max - o.min + 1;
    var maxD = PAD, minD = PAD - (count - 1) * ROW;

    var wrap = el('div.o2-wheel');
    wrap.appendChild(el('div.o2-wheel-band'));

    var list = el('ul.o2-wheel-list');
    var rows = [];
    for (var v = o.min; v <= o.max; v++) {
      var row = el('li.o2-wheel-row', { 'data-v': v },
        el('span.o2-wheel-num', { text: o.format ? o.format(v) : String(v) }));
      rows.push(row);
      list.appendChild(row);
    }
    wrap.appendChild(list);
    // The unit belongs to the BAND, not to a row: it is a label on the selection, so it
    // stays put while the numbers travel under it. Inside the rows it had to be toggled on
    // whichever row was centred, which made it flicker from row to row.
    if (o.unit) wrap.appendChild(el('span.o2-wheel-unit', { text: o.unit }));
    wrap.appendChild(el('div.o2-wheel-fade.top'));
    wrap.appendChild(el('div.o2-wheel-fade.bottom'));

    var current = Math.min(Math.max(o.value, o.min), o.max);
    var distance = PAD - (current - o.min) * ROW;
    var lit = -1;

    function indexFor(d) { return Math.min(Math.max(Math.round((PAD - d) / ROW), 0), count - 1); }
    function move(d) { list.style.transform = 'translate3d(0,' + d + 'px,0)'; }

    /** Two class writes per row crossed — the row leaving the band and the one entering. */
    function light(i) {
      if (i === lit) return;
      if (rows[lit]) rows[lit].classList.remove('near0');
      if (rows[i]) rows[i].classList.add('near0');
      lit = i;
    }

    function settle() {
      var i = indexFor(distance);
      distance = PAD - i * ROW;
      list.style.transition = 'transform .18s ease-out';
      move(distance);
      light(i);
      var value = i + o.min;
      if (value !== current) { current = value; o.onChange(value); }
    }

    var dragging = false, lastY = 0, lastT = 0, velocity = 0, moved = false, glide = null;

    wrap.addEventListener('pointerdown', function (e) {
      if (glide) { cancelAnimationFrame(glide); glide = null; }
      dragging = true; moved = false; velocity = 0;
      lastY = e.clientY; lastT = e.timeStamp;
      list.style.transition = 'none';
      wrap.setPointerCapture(e.pointerId);
    });

    wrap.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      e.preventDefault();
      var dy = e.clientY - lastY;
      var dt = e.timeStamp - lastT;
      if (dy) moved = true;
      // Smoothed so one jittery sample cannot dominate the throw.
      if (dt > 0) velocity = 0.8 * (dy / dt) + 0.2 * velocity;
      lastY = e.clientY; lastT = e.timeStamp;
      distance += dy;
      // Half resistance past either end, so the list resists rather than detaching.
      if (distance > maxD) distance = maxD + (distance - maxD) / 2;
      else if (distance < minD) distance = minD + (distance - minD) / 2;
      move(distance);
      light(indexFor(distance));
    });

    /*
     * Inertia. A flick keeps travelling and decelerates, the way an iOS picker does — the
     * previous version snapped the instant the finger left, which read as dead.
     *
     * Driven here rather than handed to the browser's scroll momentum, so the highlight
     * can be kept in step frame by frame: the earlier Swiper version could not, and rows
     * swept past the centre stayed unstyled.
     */
    var FRICTION = 0.94, MIN_V = 0.04;
    function coast(prev) {
      glide = requestAnimationFrame(function (now) {
        var dt = Math.min(now - prev, 32);
        distance += velocity * dt;
        velocity *= Math.pow(FRICTION, dt / 16);

        // Past an end, pull back hard so it cannot coast into empty space.
        if (distance > maxD || distance < minD) {
          distance = distance > maxD ? maxD : minD;
          velocity = 0;
        }
        move(distance);
        light(indexFor(distance));

        if (Math.abs(velocity) > MIN_V) coast(now);
        else { glide = null; settle(); }
      });
    }

    function release(e) {
      if (!dragging) return;
      dragging = false;
      if (wrap.hasPointerCapture && wrap.hasPointerCapture(e.pointerId)) wrap.releasePointerCapture(e.pointerId);
      // A tap with no drag selects the row under the finger.
      if (!moved) {
        var y = e.clientY - wrap.getBoundingClientRect().top;
        distance -= Math.round((y - H / 2) / ROW) * ROW;
        return settle();
      }
      if (Math.abs(velocity) > MIN_V) coast(performance.now());
      else settle();
    }
    wrap.addEventListener('pointerup', release);
    wrap.addEventListener('pointercancel', release);

    // Keyboard, since this replaces a natively scrollable element.
    wrap.tabIndex = 0;
    wrap.addEventListener('keydown', function (e) {
      var step = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0;
      if (!step) return;
      e.preventDefault();
      distance -= step * ROW;
      settle();
    });

    move(distance);
    light(indexFor(distance));
    return wrap;
  }

  /**
   * D2–D5 share this: unit toggle, a large value over a rule, screen-specific content, a
   * bottom-pinned action.
   *
   * The design splits mobile into two frames (D2 → D2b) where tapping the value opens a
   * picker, and "Save answer" closes it again. That is one screen with two states, not two
   * steps, so Back still returns to the previous question.
   *
   * `picking` can only ever be true on mobile: the value is a button there, and on web it is
   * replaced by a typed box that never opens the wheel. So the web branch always shows the
   * normal CTA without needing its own check.
   */
  function numericScreen(o, ctx) {
    /*
     * Mobile arrives with the wheel already open and the CTA reading "Save answer" — the
     * screen exists to collect one number, so making the user tap the value to reveal the
     * control is a step for nothing.
     *
     * Mobile ONLY: this flag drives the CTA's label and action, so defaulting it true
     * everywhere would leave web showing "Save answer" instead of "Next step".
     */
    var picking = !window.matchMedia('(min-width: 768px)').matches;
    var extraHost, ctaBtn, errNode, valNode;

    /**
     * Has the user actually answered?
     *
     * o.value() falls back to a sensible default so the wheel and the readout have somewhere
     * to start, but that default is NOT an answer — it was never written to the store. Left
     * unchecked, accepting the suggestion sent height, currentWeight and goalWeight up as
     * null and the DTO rejected all three at submit, on the last screen, after fifty
     * questions. Nothing advances until this is true.
     */
    function answered() { return o.stored ? o.stored() != null : true; }


    /*
     * Typing must NOT rebuild the screen — recreating the <input> drops focus and the
     * caret after every keystroke. Only the derived readout, the error line and the CTA
     * depend on the value, so only those are refreshed. Unit changes and opening the
     * picker are button presses with no focus to lose, so those rebuild this subtree.
     */
    /** Value as shown above the rule — "5ft 9in" for imperial height, else the number. */
    function display(v) {
      if (!opt(o.feetInches)) return String(v);
      var fi = C.toFeetInches(v);
      return fi.feet + 'ft ' + fi.inches + 'in';
    }

    /**
     * Range and integer check on the STORED value.
     *
     * min/max on the <input> are advisory — the browser will not stop a typed 12, and the
     * wheel cannot produce one, so nothing caught it until the server did. That failure
     * surfaced at submit, on the last screen, listing every metric at once.
     */
    function rangeError() {
      if (!answered()) return null;
      var v = o.stored();
      var min = opt(o.min), max = opt(o.max);
      if (typeof v !== 'number' || !isFinite(v) || Math.floor(v) !== v) return 'Enter a whole number';
      if (v < min || v > max) {
        if (opt(o.feetInches)) {
          var lo = C.toFeetInches(min), hi = C.toFeetInches(max);
          return 'Enter a height between ' + lo.feet + 'ft ' + lo.inches + 'in and ' + hi.feet + 'ft ' + hi.inches + 'in';
        }
        return 'Enter a value between ' + min + ' and ' + max + ' ' + o.unit();
      }
      return null;
    }

    /** Screen-specific error (D4's target floor) plus the shared range check. */
    function anyError() { return rangeError() || (o.error && o.error()) || null; }

    function refresh() {
      // The wheel writes through onChange without rebuilding, so the big value has to be
      // updated by hand — otherwise scrolling the picker leaves the number above unchanged.
      if (valNode) valNode.textContent = display(o.value());
      if (extraHost) {
        extraHost.innerHTML = '';
        // D3b/D4b hide their derived readouts while the picker is open.
        if (!picking && answered() && o.extra) {
          var x = o.extra();
          if (x) extraHost.appendChild(x);
        }
      }
      var err = anyError();
      if (errNode) errNode.textContent = err || '';
      if (errNode) errNode.style.display = err ? '' : 'none';
      // Same guard as build(): while the picker is open the CTA is "Save answer", which
      // closes it — it is not gated on the answer being complete. Without this the first
      // wheel scroll disabled the button over a consent box the user had not reached yet.
      if (ctaBtn) ctaBtn.disabled = !picking && (!answered() || !!err || (o.disabled && o.disabled()));
    }

    /*
     * Range and formatting depend on the CURRENT unit, so they must be read on every build.
     * Held as plain values they were captured once when the step first mounted, and the
     * unit toggle rebuilds through rerenderLocal() — so switching ft→cm left the wheel on
     * the imperial 48–87 range with a ~168cm value pinned to its end, still formatted as
     * feet.
     */
    function opt(v) { return typeof v === 'function' ? v() : v; }

    function build() {
      var content = [];
      var min = opt(o.min), max = opt(o.max), feetInches = opt(o.feetInches);

      if (o.units) {
        var toggle = el('div.o2-unit');
        o.units.forEach(function (u) {
          toggle.appendChild(el('button.o2-unit-opt' + (u === o.unit() ? '.is-on' : ''), {
            type: 'button', onclick: function () { o.setUnit(u); rerenderLocal(); },
          }, u));
        });
        content.push(toggle);
      }

      var value = o.value();
      var valueText = display(value);

      // Mobile: tapping the value opens the picker. Web: a typed field.
      var mobileVal = el('button.o2-bignum.only-mobile', {
        type: 'button', onclick: function () { picking = true; rerenderLocal(); },
      }, [
        (valNode = el('span.o2-bignum-v', { text: valueText })),
        feetInches ? null : el('span.o2-bignum-u', { text: o.unit() }),
      ]);

      /*
       * Imperial height is TWO fields, feet and inches. The stored value stays a single
       * inch count — that is what the store and the API expect — but a lone box showing it
       * under a "ft" label reads as a nonsense height: 175cm became "69 ft".
       */
      var boxKids;
      if (feetInches) {
        var fi = C.toFeetInches(value);
        var ftInput = el('input.o2-numinput.narrow', {
          type: 'number', inputmode: 'numeric', value: answered() ? fi.feet : '',
          oninput: function (e) {
            o.onChange(Number(e.target.value) * 12 + C.toFeetInches(o.value()).inches);
            refresh();
          },
        });
        var inInput = el('input.o2-numinput.narrow', {
          type: 'number', inputmode: 'numeric', min: 0, max: 11, value: answered() ? fi.inches : '',
          oninput: function (e) {
            o.onChange(C.toFeetInches(o.value()).feet * 12 + Math.min(Number(e.target.value), 11));
            refresh();
          },
        });
        boxKids = [
          ftInput, el('span.o2-numbox-unit.auto', { text: 'ft' }),
          el('span.o2-numbox-rule'),
          inInput, el('span.o2-numbox-unit.auto', { text: 'in' }),
        ];
      } else {
        boxKids = [
          el('input.o2-numinput', {
            /*
             * Empty until answered, then the user's OWN value — never the suggestion.
             * A greyed suggestion reads as a value already entered; an answer that vanishes
             * on return reads as lost, and leaves the derived card below showing figures the
             * empty box appears to contradict.
             */
            type: 'number', inputmode: 'numeric', value: answered() ? value : '',
            min: min, max: max,
            oninput: function (e) { o.onChange(Number(e.target.value)); refresh(); },
          }),
          el('span.o2-numbox-rule'),
          el('span.o2-numbox-unit', { text: o.unit() }),
        ];
      }

      var webVal = el('div.o2-numbox-wrap.only-web', null, [
        el('div.o2-numbox', null, boxKids),
        el('p.o2-numhint', { text: 'Type your number to continue' }),
      ]);

      /*
       * Web opens ready to type: the field is empty and focused, so the screen takes a
       * number without a click first. Focused after layout, and only when the field is
       * actually visible — on mobile this element is display:none and focusing it would
       * scroll the page to a hidden box.
       */
      requestAnimationFrame(function () {
        var first = webVal.querySelector('.o2-numinput');
        if (first && first.offsetParent) first.focus({ preventScroll: true });
      });

      content.push(el('div.o2-valblock', null, [mobileVal, el('div.o2-rule.only-mobile'), webVal]));

      if (picking) {
        var wheel = wheelPicker({
          min: min, max: max, value: value, unit: feetInches ? '' : o.unit(),
          format: feetInches ? function (v) { var f = C.toFeetInches(v); return f.feet + "'" + f.inches + '"'; } : null,
          onChange: function (v) { o.onChange(v); refresh(); },
        });
        wheel.classList.add('only-mobile');
        content.push(wheel);
      }
      // Derived readouts (D3's BMI card, D4's change card) live in a host so they can be
      // refreshed on their own while the value is being typed.
      extraHost = el('div.o2-extra');
      // The derived card belongs to the answered state: not while the picker is open, and
      // not before there is an answer to derive it from.
      if (!picking && answered() && o.extra) {
        var extra = o.extra();
        if (extra) extraHost.appendChild(extra);
      }
      content.push(extraHost);

      // D2b keeps the consent row visible while the picker is open — it is an input, and
      // hiding it leaves no way to tick it.
      if (o.persistent) content.push(o.persistent());

      var err = anyError();
      errNode = el('p.o2-err', { text: err || '' });
      if (!err) errNode.style.display = 'none';
      ctaBtn = U.primaryButton(
        picking ? 'Save answer' : (o.cta || 'Next step'),
        picking
          ? function () {
              o.onChange(o.value());
              picking = false;
              rerenderLocal();
              /*
               * Reading the wheel leaves the page scrolled down. Closing it removes 196px
               * and reveals the derived card, so without this the user lands mid-page and
               * has to scroll to see the result they just set.
               */
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          : ctx.next,
        { disabled: !picking && (!answered() || !!err || (o.disabled && o.disabled())) }
      );
      var footer = [errNode, ctaBtn];

      return U.screen({ title: o.title, gap: 28, content: content, footer: footer });
    }

    var host = el('div.o2-numhost');
    function rerenderLocal() { host.innerHTML = ''; host.appendChild(build()); }
    rerenderLocal();
    return host;
  }

  /** Caret position just after the nth digit of a masked string. */
  function caretAfterDigits(masked, n) {
    if (n <= 0) return 0;
    var seen = 0;
    for (var i = 0; i < masked.length; i++) {
      if (masked.charCodeAt(i) >= 48 && masked.charCodeAt(i) <= 57) {
        seen++;
        if (seen === n) return i + 1;
      }
    }
    return masked.length;
  }

  /**
   * DD.MM.YYYY field. Shared by D5 (past) and D8 (future) with different bounds.
   *
   * The input is built ONCE and never replaced. Rebuilding it per keystroke meant the caret
   * had to be restored by hand, and it was restored to the end of the string — so putting
   * the cursor in the month and pressing backspace deleted from the year instead.
   *
   * The caret is tracked by DIGIT COUNT rather than character offset, because masking
   * inserts and removes dots: "12.3" -> typing a digit gives "12.34", and a raw offset
   * would drift by one every time a separator appeared.
   */
  function maskedDateField(o) {
    var text = C.formatMasked(o.value());
    var touched = false;
    var host = el('div');
    var msg = el('p.o2-datelabel');

    function update() {
      var parsed = C.parseMaskedDate(text);
      var err = !touched || text.length < 10 ? null : !parsed ? 'Please enter a valid date' : o.validate(parsed);
      var label = !err && o.label ? o.label() : '';
      msg.className = err ? 'o2-err' : 'o2-datelabel';
      msg.textContent = err || label || '';
      msg.style.display = (err || label) ? '' : 'none';
    }

    var input = el('input.o2-datein', {
      type: 'text', inputmode: 'numeric', value: text, placeholder: 'DD.MM.YYYY',
      oninput: function (e) {
        var raw = e.target.value;
        var digitsBefore = (raw.slice(0, e.target.selectionStart).match(/\d/g) || []).length;

        text = C.maskDate(raw);
        // Only rewrite the field when masking actually changed it — an unnecessary write
        // moves the caret even when the text is identical.
        if (raw !== text) {
          e.target.value = text;
          var pos = caretAfterDigits(text, digitsBefore);
          e.target.setSelectionRange(pos, pos);
        }

        var p = C.parseMaskedDate(text);
        o.onCommit(p && !o.validate(p) ? p : null);
        update();
      },
      onblur: function () { touched = true; update(); },
    });

    host.appendChild(input);
    host.appendChild(el('div.o2-rule'));
    host.appendChild(msg);
    update();
    return host;
  }

  /* ─── W01 Age gate ───────────────────────────────────────────────────────── */

  /*
   * The artwork carries its own label bar and chevron, so the tile is just the image — the
   * caption and pill the placeholder version drew are baked into the file. Square at both
   * breakpoints, matching the 1:1 source.
   */
  var AGE_TILES = [
    { v: '18-29', l: 'Age: 18-29', img: 'age_18_29' },
    { v: '30-39', l: 'Age: 30-39', img: 'age_30_39' },
    { v: '40-49', l: 'Age: 40-49', img: 'age_40_49' },
    { v: '50+', l: 'Age: 50+', img: 'age_50_plus' },
  ];

  NS.custom.ageGate = function (step, ctx) {
    var grid = el('div.o2-agegrid');
    AGE_TILES.forEach(function (t) {
      grid.appendChild(el('button.o2-agetile', {
        type: 'button', 'aria-label': t.l,
        onclick: function () {
          // Tapping a tile is also the Terms/Privacy accept. The band only personalises
          // W06 copy — D5 asks the exact date later and wins on conflict.
          NS.store.set('aboutYou', { ageGroup: t.v });
          NS.store.set('auth', { termsAccepted: true });
          ctx.next();
        },
      }, el('img', {
        src: NS.sized(NS.asset(t.img), 400),
        srcset: NS.sized(NS.asset(t.img), 300) + ' 300w, ' + NS.sized(NS.asset(t.img), 600) + ' 600w',
        sizes: '(min-width: 768px) 25vw, 50vw',
        alt: '', decoding: 'async',
      })));
    });

    var legal = el('p.o2-legal', {
      html: 'By choosing your age and continuing you agree to our ' +
        '<a href="/policies/terms-of-service">Terms of Service</a> and ' +
        '<a href="/policies/privacy-policy">Privacy Policy</a>.',
    });

    return el('main.o2-main.o2-agemain', null,
      el('div.o2-col.wide.o2-agecol', null, [
        el('div.o2-titles', null, [
          el('h1.o2-title', { text: 'Your at-home Pilates plan' }),
          el('p.o2-sub', { text: 'Based on your age' }),
        ]),
        grid,
        // Mobile pins the legal line to the bottom with a spacer; web has none, so it
        // simply follows the tiles.
        el('div.o2-spacer.only-mobile'),
        legal,
      ]));
  };

  /* ─── D2 Height ──────────────────────────────────────────────────────────── */

  NS.custom.height = function (step, ctx) {
    return numericScreen({
      title: 'How tall are you?',
      units: ['ft', 'cm'],
      unit: function () { return S().metrics.height.unit === 'cm' ? 'cm' : 'ft'; },
      setUnit: function (u) { C.setHeightUnit(u === 'cm' ? 'cm' : 'in'); },
      feetInches: function () { return S().metrics.height.unit !== 'cm'; },
      value: function () {
        var h = S().metrics.height;
        return h.value == null ? (h.unit === 'cm' ? 168 : 66) : h.value;
      },
      // Raw store value — null until the user commits one. See answered().
      stored: function () { return S().metrics.height.value; },
      onChange: function (v) { NS.store.set('metrics', { height: { value: v, unit: S().metrics.height.unit } }); },
      // 50 rather than 48 inches: the server validates height as 50-300 in whatever unit is
      // submitted, so anything under 50in would pass here and fail at the DTO.
      min: function () { return S().metrics.height.unit === 'cm' ? 120 : 50; },
      max: function () { return S().metrics.height.unit === 'cm' ? 220 : 87; },
      disabled: function () { return !S().auth.healthDataConsent; },
      persistent: function () {
        var checked = !!S().auth.healthDataConsent;
        var box = el('span.o2-checkbox' + (checked ? '.is-on' : ''), null, checked ? svg(U.ICON.tick) : null);
        return el('button.o2-consent', {
          type: 'button', 'aria-pressed': checked ? 'true' : 'false',
          onclick: function (e) {
            // Toggled in place — rebuilding the screen here would restart its enter
            // animation and flash the whole page for a checkbox.
            checked = !checked;
            NS.store.set('auth', { healthDataConsent: checked });
            box.classList.toggle('is-on', checked);
            box.innerHTML = '';
            if (checked) box.appendChild(svg(U.ICON.tick));
            e.currentTarget.setAttribute('aria-pressed', checked ? 'true' : 'false');
            var btn = e.currentTarget.closest('.o2-numhost');
            btn = btn && btn.querySelector('.o2-btn');
            if (btn) btn.disabled = !checked;
          },
        }, [
          box,
          el('span.o2-consent-text', {
            html: 'I agree to Reformly processing my health details to build my plan. ' +
              '<a href="/policies/privacy-policy">Privacy Policy</a>.',
          }),
        ]);
      },
    }, ctx);
  };

  /* ─── D3 Weight ──────────────────────────────────────────────────────────── */

  NS.custom.weight = function (step, ctx) {
    var kg = function () { return S().metrics.currentWeight.unit === 'kg'; };
    return numericScreen({
      title: 'What do you weigh right now?',
      units: ['lbs', 'kg'],
      unit: function () { return kg() ? 'kg' : 'lbs'; },
      setUnit: function (u) { C.setWeightUnit(u === 'kg' ? 'kg' : 'lb'); },
      value: function () {
        var w = S().metrics.currentWeight;
        return w.value == null ? (w.unit === 'kg' ? 74 : 163) : w.value;
      },
      stored: function () { return S().metrics.currentWeight.value; },
      onChange: function (v) { NS.store.set('metrics', { currentWeight: { value: v, unit: S().metrics.currentWeight.unit } }); },
      min: function () { return kg() ? 35 : 77; },
      max: function () { return kg() ? 200 : 440; },
      extra: function () {
        var bmi = C.bmi();
        if (!bmi) return null;
        var band = { underweight: 'below range', normal: 'in range', overweight: 'just above range', obese: 'above range' }[C.bmiCategory(bmi)];
        var m = S().metrics;
        return statCard({
          label: 'Your BMI',
          value: bmi.toFixed(1) + ', ' + band,
          body: 'Based on ' + C.formatHeight(m.height.value, m.height.unit) + ' and ' +
            m.currentWeight.value + ' ' + m.currentWeight.unit +
            '. We use it to set the pace of your plan, and you will never see a weigh-in prompt in the app.',
        });
      },
    }, ctx);
  };

  /* ─── D4 Target ──────────────────────────────────────────────────────────── */

  NS.custom.target = function (step, ctx) {
    var kg = function () { return S().metrics.goalWeight.unit === 'kg'; };
    // Suggested target: a 12% drop, matching the design's worked example.
    var goal = function () {
      var g = S().metrics.goalWeight;
      if (g.value != null) return g.value;
      var cur = S().metrics.currentWeight.value || (kg() ? 74 : 163);
      return Math.round(cur * 0.88);
    };

    return numericScreen({
      title: 'And what would feel good to you?',
      units: ['lbs', 'kg'],
      unit: function () { return kg() ? 'kg' : 'lbs'; },
      setUnit: function (u) { C.setWeightUnit(u === 'kg' ? 'kg' : 'lb'); },
      value: goal,
      stored: function () { return S().metrics.goalWeight.value; },
      onChange: function (v) { NS.store.set('metrics', { goalWeight: { value: v, unit: S().metrics.goalWeight.unit } }); },
      min: function () { return kg() ? 35 : 77; },
      max: function () { return kg() ? 200 : 440; },
      extra: function () {
        var cur = S().metrics.currentWeight.value || 0;
        var diff = cur - goal();
        if (diff <= 0) return null;
        var pct = Math.round((diff / cur) * 100);
        var unit = S().metrics.goalWeight.unit;
        /*
         * The copy follows the size of the drop. With the floor guard gone any target is
         * accepted, and calling a 27% drop "a healthy pace" is both wrong and a promise
         * Reformly should not be making. Up to 12% is the design's own wording, kept
         * verbatim — that is the case the mockup shows.
         */
        var note, body;
        if (pct <= 12) {
          note = 'A ' + pct + '% drop is a healthy pace';
          body = 'Most women reach a change like this in how their clothes fit within the first sixty days, without cutting anything out.';
        } else if (pct <= 20) {
          note = 'A ' + pct + '% drop is a bigger goal';
          body = 'That is more than most start with, so your plan builds towards it rather than rushing the first few weeks.';
        } else {
          note = 'A ' + pct + '% drop is a long-term goal';
          body = 'Changes this size are best taken slowly. Your plan starts where you are now and adds as you get stronger, with no deadline on it.';
        }

        return statCard({
          label: 'Change from ' + cur + ' ' + unit,
          value: diff + ' ' + unit + ', ' + pct + '%',
          note: note,
          body: body,
        });
      },
    }, ctx);
  };

  /* ─── D5 Date of birth ───────────────────────────────────────────────────── */

  NS.custom.dob = function (step, ctx) {
    var MIN_AGE = 18, MAX_AGE = 90;
    // Held so onCommit can re-enable it in place. Reading validity once while building the
    // footer left the button stuck in whatever state the screen was entered with, so a
    // corrected date never unlocked Next.
    var cta;
    var field = maskedDateField({
      value: function () { return S().aboutYou.birthday; },
      label: function () {
        var d = S().aboutYou.birthday;
        return d ? C.ageFrom(d) + ' years old' : '';
      },
      validate: function (d) {
        var a = C.ageFrom(d);
        if (a < MIN_AGE) return 'You need to be ' + MIN_AGE + ' or over to use Reformly';
        if (a > MAX_AGE) return 'Please check the year';
        return null;
      },
      onCommit: function (d) {
        if (!d) {
          NS.store.set('aboutYou', { birthday: null });
        } else {
          // D5 wins over the W01 gate and the band is recomputed from it, so W06's copy
          // stays honest.
          NS.store.set('aboutYou', { birthday: d, ageGroup: C.ageGroupFor(C.ageFrom(d)) });
        }
        if (cta) cta.disabled = !d;
      },
    });

    // Green check here too, matching the country screen. The design file has this card
    // plain, but the checked variant is what the rest of the funnel uses.
    var why = noticeCard(
      'Why we ask',
      el('p.o2-stat-body', { text: 'Your plan adjusts the pace and the recovery days to your age, so nothing feels like too much in week one.' })
    );

    return U.screen({
      title: 'When is your birthday?', gap: 36,
      content: [field, why],
      footer: (cta = U.primaryButton('Next step', ctx.next, {
        disabled: !S().aboutYou.birthday,
      })),
    });
  };

  /* ─── D8 Event date ──────────────────────────────────────────────────────── */

  NS.custom.eventDate = function (step, ctx) {
    var cta;
    var field = maskedDateField({
      value: function () { return S().plan.specialEventDate; },
      label: function () {
        var d = S().plan.specialEventDate;
        return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
      },
      validate: function (d) {
        var days = Math.floor((d.getTime() - Date.now()) / 86400000);
        if (days < 0) return 'Pick a date in the future';
        // D9 builds its curve backwards from this, so a date years out renders a flat line.
        if (days > 730) return 'Pick a date within the next two years';
        return null;
      },
      onCommit: function (d) {
        NS.store.set('plan', { specialEventDate: d });
        if (cta) cta.disabled = !d;
      },
    });

    return U.screen({
      title: 'When is it?',
      subtitle: 'We will build the plan backwards from that date.',
      gap: 36,
      content: field,
      footer: [
        (cta = U.primaryButton('Continue', ctx.next, { disabled: !S().plan.specialEventDate })),
        U.textButton('Skip this step', function () {
          NS.store.set('plan', { specialEventDate: null });
          ctx.next();
        }),
      ],
    });
  };

  NS.custom.referralLanding = function (s, c) { return NS.customExtra.referralLanding(s, c); };
  NS.custom.profile = function (s, c) { return NS.customExtra.profile(s, c); };
  NS.custom.outlook = function (s, c) { return NS.customExtra.outlook(s, c); };
  NS.custom.email = function (s, c) { return NS.customExtra.email(s, c); };
  NS.custom.optIn = function (s, c) { return NS.customExtra.optIn(s, c); };
  NS.custom.displayName = function (s, c) { return NS.customExtra.displayName(s, c); };
  NS.custom.username = function (s, c) { return NS.customExtra.username(s, c); };
  NS.custom.country = function (s, c) { return NS.customExtra.country(s, c); };
  NS.custom.building = function (s, c) { return NS.customExtra.building(s, c); };
  NS.custom.planReady = function (s, c) { return NS.customExtra.planReady(s, c); };
  NS.custom.scratch = function (s, c) { return NS.customExtra.scratch(s, c); };

  NS.pieces = { noticeCard: noticeCard, statCard: statCard, wheelPicker: wheelPicker, maskedDateField: maskedDateField };
})(window.ReformlyQuiz);
